import React from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
} from "@material-ui/core";

import {
  DELIVERY_TYPE,
  ZONE_CHANGE_STATUS,
} from "../../../../config/constants";
import { PosStatusToggle } from "../../../POSList/posUtils/PosAccessUtils";
import { is_zone_service_toggle_visible } from "../../CompleteSetupUtils";
import { getKeyNameFromZoneTab } from "../../../../utils/ServiceTypeUtils";
import { POS_ZONE_STATE_STATUS } from "../../../ServiceAndSlots/utils/constants";
import { getServicePropositionDeliveryType } from "../../../../utils/helperFunctions";

import Loader from "../../../common/Loader/Loader";

import ExpandMoreIcon from "../../../assets/svg/ExpandAccordionIcon.svg";
import RenderDefaultSupplierAllInformation from "../../../DefaultServiceType/RenderDefaultSupplierAllInformation";
import RenderCollectionPoints from "../../../ClickAndCollectServiceType/RenderCollectionPoints";
import RenderServiceAllInformation from "./RenderServiceAllInformation";
import { generateElementIdentifier } from "utils/ApplicationUtils";
import { DATA_IDENTIFIER, ELEMENT } from "components/common/App/app-constant";

interface DetailedSlotInformationOnCompleteSetupInterface {
  classes: any;
  userInfo: any;
  toggling: any;
  tab: any;
  tabIndex: any;
  serviceTypeName: any;
  handleActivateDeactivateZoneConfigClick: any;
  handleCollectionPointToggleSwitch: any;
  servicePropositionList: any;
}

export const DetailedSlotInformationOnCompleteSetup = (
  props: DetailedSlotInformationOnCompleteSetupInterface
) => {
  const {
    classes,
    userInfo,
    toggling,
    tab,
    tabIndex,
    serviceTypeName,
    handleActivateDeactivateZoneConfigClick,
    handleCollectionPointToggleSwitch,
    servicePropositionList,
  } = props;

  const showCapacityFlag = () => {
    return (
      getServicePropositionDeliveryType(
        servicePropositionList,
        serviceTypeName
      ) !== DELIVERY_TYPE.DAY_BASED
    );
  };

  const renderZoneNameTextStyle = () => {
    const zoneName = getKeyNameFromZoneTab(tab, "zoneName");
    if (showCapacityFlag()) {
      return `${zoneName} (Capacity - ${tab?.capacityAmountAtZoneLevel ?? "0"
        })`;
    }
    return zoneName;
  };

  const renderStatusMessage = () => {
    const ServicePropositionDeliveryType = getServicePropositionDeliveryType(
      servicePropositionList,
      serviceTypeName
    );
    const status = getKeyNameFromZoneTab(tab, "status");
    const isMinuteBasedAndActivated =
      ServicePropositionDeliveryType === DELIVERY_TYPE.MINUTE_BASED &&
      status === ZONE_CHANGE_STATUS.ACTIVATED;

    if (!isMinuteBasedAndActivated) {
      return null;
    }

    const zoneState = getKeyNameFromZoneTab(tab, "state");
    if (zoneState === POS_ZONE_STATE_STATUS.AVAILABLE) {
      return (
        <Typography
          data-testid={generateElementIdentifier({
            key: DATA_IDENTIFIER.ACCEPTING_ORDERS,
            element: ELEMENT.TYPOGRAPHY,
          })}
          className={classes.availableZoneSpanStyle}
        >{`Accepting Orders`}</Typography>
      );
    } else {
      return (
        <Typography
          className={classes.busyZoneSpanStyle}
        >{`Not Accepting Orders`}</Typography>
      );
    }
  };

  const renderZoneServiceToggle = () => {
    if (!is_zone_service_toggle_visible(userInfo)) {
      return null;
    }

    if (toggling) {
      return (
        <Grid item xs={5} className={classes.posRelative}>
          <Loader />
        </Grid>
      );
    } else {
      const isActivated =
        getKeyNameFromZoneTab(tab, "status") === ZONE_CHANGE_STATUS.ACTIVATED;
      const id = getKeyNameFromZoneTab(tab, "id");
      return (
        <PosStatusToggle
          userInfo={userInfo}
          classes={classes}
          checked={isActivated}
          handleServiceActiveInactiveSwitch={(e: any) =>
            handleActivateDeactivateZoneConfigClick(e, tabIndex, id)
          }
          disabled={toggling}
        />
      );
    }
  };

  const renderAccordionContent = () => {
    const deliveryType = getServicePropositionDeliveryType(
      servicePropositionList,
      serviceTypeName
    );
    switch (deliveryType) {
      case DELIVERY_TYPE.DAY_BASED:
        return (
          <RenderDefaultSupplierAllInformation
            classes={classes}
            supplierTypes={tab?.supplierTypes}
            serviceTypeName={serviceTypeName}
            holidayConfig={tab?.holidayConfig}
          />
        );
      case DELIVERY_TYPE.CLICK_N_COLLECT:
        return (
          <RenderCollectionPoints
            classes={classes}
            tab={tab}
            collectionPoints={tab?.collectionPoints}
            userInfo={userInfo}
            handleCollectionPointToggleSwitch={
              handleCollectionPointToggleSwitch
            }
            serviceTypeName={serviceTypeName}
            servicePropositionList={servicePropositionList}
          />
        );
      default:
        return (
          <RenderServiceAllInformation
            classes={classes}
            tab={tab}
            serviceTypeName={serviceTypeName}
            servicePropositionList={servicePropositionList}
          />
        );
    }
  };

  return (
    <Accordion
      key={tab.zoneConfigId || ""}
      className={classes.zoneAccordionStyle}
    >
      <AccordionSummary
        expandIcon={<img src={ExpandMoreIcon} alt="Expand Accordion Icon" />}
        className={`${classes.accordionSummary} ${classes.serviceAccSummary}`}
      >
        <Grid container alignItems="center">
          <Grid item xs={6}>
            <Typography
              data-testid={generateElementIdentifier({
                key: DATA_IDENTIFIER.ZONE_NAME,
                element: ELEMENT.TYPOGRAPHY,
              })}
              className={classes.zoneNameTextStyle}
            >{`${renderZoneNameTextStyle()}`}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Grid container justifyContent="flex-end">
              {renderStatusMessage()}
              {renderZoneServiceToggle()}
            </Grid>
          </Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails
        className={classes.serviceAccDetails}
        onClick={(e) => e.stopPropagation()}
      >
        {renderAccordionContent()}
      </AccordionDetails>
    </Accordion>
  );
};

export default React.memo(DetailedSlotInformationOnCompleteSetup);
